import React, { useEffect, useState } from "react";

import { Box, Chip, Grid, MenuItem, OutlinedInput, SelectChangeEvent } from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Input, Select } from "Components/Form";
import SelectMultiple from "Components/Form/SelectMultiple";
import Modal from "Components/Generic/Modal";
import SkeletonComponent from "Components/Generic/Skeleton";
import { useAppState } from "Context/StateContext";
import useYupValidation from "Hooks/useYupValidation";
import { Applications } from "Models/applications";
import { Branch } from "Models/branch";
import { Project } from "Models/project";
import ApplicationsService from "Services/applications.service";
import BranchService from "Services/branch.service";
import ProjectService from "Services/project.service";
import { Status } from "Util/enums";
import { handleValidation } from "Util/formValidation";
import { ApplicationKeys, BranchKeys } from "Util/queries";
import { getTypes } from "../types";
import useStyles from "./styles";
import { Group } from "Models/group";
import GroupService from "Services/group.service";

type ModalToInviteUserProps = {
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onInviteMember: (
    email: string,
    groupId: number,
    typeUser: string,
    code: string,
    branches: Branch[],
    applications?: Applications[]
  ) => void;
};

export const ModalToInviteMember = ({
  isOpen,
  isLoading,
  onClose,
  onInviteMember,
}: ModalToInviteUserProps) => {
  const { t: translate } = useTranslation();

  useYupValidation
  const { currentUser, isFMVersion, isMovingVersion } = useAppState();

  const classes = useStyles();
  const [userEmail, setUserEmail] = useState<string>("");
  const [groupSelected, setGroupSelected] = useState<Group>();
  const [isValidUserEmail, setIsValidUserEmail] = useState<boolean>(false);
  const [typeUser, setTypeUser] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [selectedBranches, setSelectedBranches] = useState<Branch[]>([]);
  const [selectedApplications, setSelectedApplications] = useState<Applications[]>([]);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [projectList, setProjectList] = useState<Project[]>([]);
  const [groups, setGroups] = useState<Group[]>();
  const [selectedProjects, setSelectedProjects] = useState<Project[]>(
    isFMVersion ? [] : currentUser?.projects || []
  );
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  useQuery(
    ["groups.list"],
    () =>
      GroupService.getGroups({
        status: 1,
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response?.data && response?.data?.length > 0) setGroups(response.data);
      },
    }
  );
  const { isFetching: isFetchingProjects } = useQuery(
    ["projects.list"],
    () =>
      ProjectService.getAllNetwork({
        attributes: ["id", "name", "companyId"],
        companyId: currentUser?.companyId,
        group: ["projects.id"],
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: isFMVersion,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setProjectList(response.data.rows);
      },
    }
  );

  const { data: branchList, isFetching: isFetchingBranches } = useQuery({
    queryKey: BranchKeys.getAll(["invite-member-modal"]),
    queryFn: async () => {
      const response = await BranchService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const { data: applicationsList, isFetching: isFetchingApplications } = useQuery({
    queryKey: ApplicationKeys.getAll(["invite-member-modal"]),
    queryFn: async () => {
      const response = await ApplicationsService.getAll({
        include: JSON.stringify([
          {
            association: "profiles",
            where: {
              initials: currentUser?.company?.profile?.initials,
            },
          },
        ]),
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const schema = yup.object().shape({
    userEmail: yup.string().email().required(),
    code: yup.string().trim().required(translate("thisFieldIsRequired")),
    branches: yup
      .array()
      .min(1, translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    applications: yup.array().notRequired(),
    isFM: yup.boolean(),
    projects: yup.array().when("isFM", {
      is: true,
      then: yup
        .array()
        .of(
          yup.object().shape({
            id: yup.number().typeError(translate("thisFieldIsRequired")),
            name: yup.string().typeError(translate("thisFieldIsRequired")),
          })
        )
        .min(1, translate("thisUserNeedsToHaveSomeProjectSelected"))
        .required(translate("thisUserNeedsToHaveSomeProjectSelected")),
    }),
  });

  const onSubmit = () => {
    if (
      isValidUserEmail &&
      groupSelected &&
      groupSelected?.id &&
      typeUser &&
      code.trim() &&
      selectedBranches?.length > 0 &&
      !fieldHasError("code") &&
      !fieldHasError("branches") &&
      !fieldHasError("projects")
    ) {
      onInviteMember(
        userEmail,
        groupSelected?.id,
        typeUser,
        code.trim(),
        selectedBranches,
        selectedApplications?.length ? selectedApplications : undefined
      );
    }
  };

  const markFieldAsTouched = (field: string) =>
    setTouchedFields((state) => ({ ...state, [field]: true }));

  const showFieldError = (field: string) => !!touchedFields[field] && fieldHasError(field);

  useEffect(() => {
    handleValidation({
      schema,
      validateSyncObject: {
        userEmail,
        code,
        branches: selectedBranches,
        applications: selectedApplications,
        isFM: isFMVersion,
        projects: selectedProjects,
      },
      updateErrors: (err: any) => {
        if (err && err?.length > 0) {
          setIsValidUserEmail(false);
          setErrors(err);

          return;
        }

        if (err?.length === 0) {
          setIsValidUserEmail(true);
          setErrors([]);
        }
      },
    });
  }, [userEmail, selectedProjects, code, selectedBranches, selectedApplications]);
  
  const renderGroups = () => {
    if (groups && groups?.length > 0) {
      return groups?.map((group) => (
        <MenuItem key={group.id} value={group?.id} data-cy={group.name}>
          {group.name}
        </MenuItem>
      ));
    }

    return null;
  };

  const renderTypeUser = () => {
    const types = getTypes(isMovingVersion, isFMVersion);

    return types?.map((type: any) => (
      <MenuItem data-cy={type.name} key={type.name} value={type.value}>
        {type.name}
      </MenuItem>
    ));
  };

  const renderChipValueBranches = () => {
    return (
      <Box className={classes.chipBoxProject}>
        {selectedBranches?.map((branch: Branch) => (
          <Chip key={branch.id} label={branch.name} className={classes.chipProject} />
        ))}
      </Box>
    );
  };

  const onSelectedBranches = (branchesId: number[]) => {
    markFieldAsTouched("branches");

    if (branchesId.includes(0)) {
      setSelectedBranches(branchList ? [...branchList] : []);

      return;
    }

    if (branchesId.includes(-1)) {
      setSelectedBranches([]);

      return;
    }

    setSelectedBranches(
      branchList?.filter((branch) => branchesId.includes(Number(branch.id))) || []
    );
  };

  const renderChipValueProjects = () => {
    return (
      <Box className={classes.chipBoxProject}>
        {selectedProjects?.map((value: Project) => {
          return <Chip key={value.id} label={value.name} className={classes.chipProject} />;
        })}
      </Box>
    );
  };

  return (
    <Modal
      title={translate("inviteMember")}
      open={isOpen}
      className={classes.modalWidth}
      showActionButtons
      actionButtonProps={{
        textSave: translate("inviteMember"),
        onSave: onSubmit,
        saveDisabled:
          !isValidUserEmail ||
          !groupSelected ||
          !typeUser ||
          fieldHasError("code") ||
          fieldHasError("branches") ||
          fieldHasError("projects"),
        loading: isLoading,
      }}
      handleClose={onClose}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Input
            fullWidth
            label={translate("userEmail")}
            autoComplete="off"
            name="userEmail"
            data-cy="userEmail"
            placeholder="sales@shipeezi.com"
            disabled={isLoading}
            value={userEmail || ""}
            type="email"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setUserEmail(event.target.value)
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Input
            fullWidth
            required
            label={translate("code")}
            autoComplete="off"
            name="code"
            data-cy="code"
            placeholder={translate("typeHere")}
            disabled={isLoading}
            value={code || ""}
            error={showFieldError("code")}
            errorMessage={getErrorByField("code")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              markFieldAsTouched("code");
              setCode(event.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            disabled={isLoading}
            label={translate("typeOfUser")}
            name="typeUser"
            value={typeUser}
            onChange={(event: SelectChangeEvent<any>) => setTypeUser(event.target.value)}
            displayEmpty
            placeholder={translate("select")}
            data-cy="typeUser"
          >
            {renderTypeUser()}
          </Select>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            name="group"
            label={translate("group")}
            value={groupSelected?.id}
            disabled={isLoading}
            onChange={(event: any) => {
              if (groups && groups?.length > 0) {
                const group = groups?.find((group) => group?.id === Number(event.target.value));

                if (group) setGroupSelected(group);
              }
            }}
            displayEmpty
            placeholder={translate("select")}
            data-cy="userGroup"
          >
            {renderGroups()}
          </Select>
        </Grid>
        <Grid item xs={12} sm={6}>
          <SkeletonComponent isLoading={isFetchingBranches}>
            <Select
              dataCy="selectBranch"
              className={classes.select}
              name="branchList"
              label={translate("branch")}
              required
              disabled={isLoading}
              value={
                selectedBranches?.length ? selectedBranches?.map((branch) => branch?.id || -2) : []
              }
              error={showFieldError("branches")}
              errorMessage={getErrorByField("branches")}
              onChange={(event: SelectChangeEvent<any>) => onSelectedBranches(event.target.value)}
              input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
              multiple
              renderValue={() => renderChipValueBranches()}
              placeholder={translate("select")}
            >
              {selectedBranches?.length < Number(branchList?.length) && (
                <MenuItem value={0}>
                  <span className={classes.listSpan}>
                    <AddCircleOutline className="mr-3" />
                    {translate("addAll")}
                  </span>
                </MenuItem>
              )}
              {!!branchList?.length && selectedBranches?.length === branchList?.length && (
                <MenuItem value={-1}>
                  <span className={classes.removeListSpan}>
                    <RemoveCircleOutline className="mr-3" />
                    {translate("removeAll")}
                  </span>
                </MenuItem>
              )}
              {branchList?.map((branch) => (
                <MenuItem data-cy={branch.name} value={branch.id} key={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </SkeletonComponent>
        </Grid>
        <Grid item xs={12}>
          <SelectMultiple
            loading={isFetchingApplications}
            dataCy="selectApplications"
            className={classes.select}
            name="accessTo"
            label={translate("accessTo")}
            disabled={isLoading}
            placeholder={translate("select")}
            value={selectedApplications?.map((application) => application.id || -2) || []}
            selecteds={selectedApplications?.map((application) => ({
              key: Number(application.id),
              label: translate(application.name),
            }))}
            renderInternalCount={false}
            onChange={(event: SelectChangeEvent<any>) => {
              if (event.target.value.includes(-2) || event.target.value.includes(0)) return;

              setSelectedApplications(
                applicationsList?.filter((application) =>
                  event.target.value.includes(application.id)
                ) || []
              );
            }}
            onAddAll={() => setSelectedApplications(applicationsList || [])}
            onRemoveAll={() => setSelectedApplications([])}
            items={
              applicationsList?.map((application) => ({
                key: Number(application.id),
                label: translate(application.name),
              })) || []
            }
          />
        </Grid>
        {isFMVersion && (
          <Grid item container direction="row" xs={12} alignItems="center" spacing={2}>
            <Grid item xs={11}>
              <SkeletonComponent isLoading={isFetchingProjects || isLoading}>
                <Select
                  dataCy="selectProjects"
                  className={classes.select}
                  name="projectList"
                  label={translate("projects")}
                  value={
                    selectedProjects && selectedProjects?.length
                      ? selectedProjects?.map((project) => project?.id || -2)
                      : []
                  }
                  onChange={(event: SelectChangeEvent<any>) => {
                    if (event.target.value.includes(0) || event.target.value.includes(-1)) return;
                    const newProjects = projectList
                      ?.filter((project) => event.target.value.includes(project.id))
                      ?.map((project) => ({
                        ...project,
                        companyId:
                          project && project.networks && project.networks.length
                            ? project?.networks[0]?.associatedCompanyId
                            : project?.companyId,
                      }));

                    setSelectedProjects(newProjects);
                  }}
                  input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                  multiple
                  renderValue={() => renderChipValueProjects()}
                >
                  {selectedProjects.length < projectList?.length && (
                    <MenuItem
                      value={0}
                      onClick={() => {
                        setSelectedProjects(projectList);
                      }}
                    >
                      <span className={classes.listSpan}>
                        <AddCircleOutline className="mr-3" />
                        {translate("addAll")}
                      </span>
                    </MenuItem>
                  )}
                  {selectedProjects.length === projectList?.length && (
                    <MenuItem
                      value={-1}
                      onClick={() => {
                        setSelectedProjects([]);
                      }}
                    >
                      <span className={classes.removeListSpan}>
                        <RemoveCircleOutline className="mr-3" />
                        {translate("removeAll")}
                      </span>
                    </MenuItem>
                  )}
                  {projectList.map((project) => (
                    <MenuItem data-cy={project.name} value={project.id} key={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </SkeletonComponent>
            </Grid>
            {projectList.length > 0 && !(isFetchingProjects || isLoading) && (
              <Grid
                item
                xs={1}
                sm={1}
                md={1}
                lg={1}
                direction="row"
                alignItems="flex-end"
                className={classes.chipMargin}
              >
                <Chip label={selectedProjects.length} className={classes.chipLabelProject} />
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </Modal>
  );
};
